

import React, { useEffect, useState } from "react";
import gurukulService from "../services/gurukulService";
import "../styles/AdmissionPage.css";
import * as XLSX from "xlsx";
import { saveAs } from "file-saver";


const AdmissionPage = () => {
  const [admissions, setAdmissions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [modalData, setModalData] = useState({ type: null, data: null });
  const [selectedGurukul, setSelectedGurukul] = useState("All");


  useEffect(() => {
    fetchAdmissions();
  }, []);

  const fetchAdmissions = async () => {
    try {
      const response = await gurukulService.getAdmissions();
      setAdmissions(response);
    } catch (error) {
      console.error("Error fetching admissions:", error);
      setError("Failed to load admissions");
    } finally {
      setLoading(false);
    }
  };

  const openModal = (type, data) => {
    setModalData({ type, data });
  };

  const closeModal = () => {
    setModalData({ type: null, data: null });
  };


  const handleDelete = async (id) => {
    const isConfirmed = window.confirm("Are you sure you want to delete this admission?");
    if (!isConfirmed) return; // If user cancels, do nothing
  
    try {
      await gurukulService.deleteAdmission(id);
      setAdmissions(admissions.filter((admission) => admission.id !== id));
    } catch (error) {
      console.error("Error deleting admission:", error);
      setError("Failed to delete admission");
    }
  };
  

  const handleUpdate = async (updatedData) => {
    try {
      const response = await gurukulService.updateAdmission(updatedData.id, updatedData);
      // Update the admission in the state
      setAdmissions(
        admissions.map((admission) =>
          admission.id === updatedData.id ? response : admission
        )
      );
      closeModal();
    } catch (error) {
      console.error("Error updating admission:", error);
      setError("Failed to update admission");
    }
  };


    // Function to export admissions to Excel
    const downloadExcel = () => {
      const filteredAdmissions = admissions.filter(
        (item) => selectedGurukul === "All" || item.gurukul === selectedGurukul
      );
  
      const worksheet = XLSX.utils.json_to_sheet(filteredAdmissions);
      const workbook = XLSX.utils.book_new();
      XLSX.utils.book_append_sheet(workbook, worksheet, "Admissions");
  
      // Generate Excel file and trigger download
      const excelBuffer = XLSX.write(workbook, { bookType: "xlsx", type: "array" });
      const data = new Blob([excelBuffer], { type: "application/octet-stream" });
      saveAs(data, "admissions_list.xlsx");
    };

  return (
    <>
      <div className="filter-container">
        <label htmlFor="gurukulFilter">Filter by Gurukul:</label>
        <select
          id="gurukulFilter"
          value={selectedGurukul}
          onChange={(e) => setSelectedGurukul(e.target.value)}
        >
          <option value="All">All</option>
          {Array.from(new Set(admissions.map((item) => item.gurukul))).map((gurukul) => (
            <option key={gurukul} value={gurukul}>
              {gurukul}
            </option>
          ))}
        </select>
      </div>

      {/* Button to Download Excel */}
      <button className="download-excel-button" onClick={downloadExcel}>
        📥 Download Excel
      </button>
  
      <div className="admission-container">
        <h2 className="admission-heading">📌 Admission List</h2>
  
        {loading && <p className="loading-message">Loading admissions...</p>}
        {error && <p className="error-message">{error}</p>}
  
        {!loading && !error && (
          <table className="admission-table">
            <thead>
              <tr>
                <th>#</th>
                <th>गुरुकुल</th>
                <th>कक्षा</th>
                <th>छात्र नाम</th>
                <th>पिता का नाम</th>
                <th>गाँव</th>
                <th>तहसील</th>
                <th>जिला</th>
                <th>राज्य</th>
                <th>संपर्क</th>
                <th>लिंग</th>
                <th>Txn Details</th>
                <th>स्थिति</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {admissions
                .filter((item) => selectedGurukul === "All" || item.gurukul === selectedGurukul)
                .map((item, index) => (
                  <tr 
                    key={item.id} 
                    className={`admission-row ${item.status === "PENDING" ? "row-pending" : "row-approved"}`}
                  >
                    <td>{index + 1}</td>
                    <td>{item.gurukul}</td>
                    <td>{item.classLevel}</td>
                    <td>{item.studentName}</td>
                    <td>{item.fatherName}</td>
                    <td>{item.village}</td>
                    <td>{item.tehsil}</td>
                    <td>{item.district}</td>
                    <td>{item.state}</td>
                    <td>{item.contact}</td>
                    <td>{item.gender}</td>
                    <td>{item.transactionDetails}</td>
                    <td className={item.status === "PENDING" ? "status-pending" : "status-approved"}>
                      {item.status}
                    </td>
                    <td className="action-buttons">
                      <button className="view-more-button" onClick={() => openModal("view", item)}>
                        View More
                      </button>
                      <button className="edit-button" onClick={() => openModal("edit", item)}>
                        Edit
                      </button>
                      <button className="delete-button" onClick={() => handleDelete(item.id)}>
                        Delete
                      </button>
                    </td>
                  </tr>
                ))}
            </tbody>
          </table>
        )}
  
        {modalData.type === "view" && modalData.data && (
          <div className="modal-overlay">
            <div className="modal-content">
              <h3>Admission Details</h3>
              {Object.entries(modalData.data).map(([key, value]) => (
                <p key={key}>
                  <strong>{key}:</strong> {value}
                </p>
              ))}
              <button className="close-button" onClick={closeModal}>
                Close
              </button>
            </div>
          </div>
        )}
  
        {modalData.type === "edit" && modalData.data && (
          <div className="modal-overlay">
            <div className="modal-content">
              <h3>Edit Admission</h3>
              <form
                onSubmit={(e) => {
                  e.preventDefault();
                  const formData = new FormData(e.target);
                  const updatedData = {
                    id: modalData.data.id,
                    gurukul: formData.get("gurukul"),
                    classLevel: formData.get("classLevel"),
                    studentName: formData.get("studentName"),
                    fatherName: formData.get("fatherName"),
                    village: formData.get("village"),
                    tehsil: formData.get("tehsil"),
                    district: formData.get("district"),
                    state: formData.get("state"),
                    contact: formData.get("contact"),
                    gender: formData.get("gender"),
                    transactionDetails: formData.get("transactionDetails"),
                    status: formData.get("status"),
                  };
                  handleUpdate(updatedData);
                }}
              >
                <label>
                  Gurukul:
                  <input type="text" name="gurukul" defaultValue={modalData.data.gurukul} required />
                </label>
                <label>
                  Class Level:
                  <input type="text" name="classLevel" defaultValue={modalData.data.classLevel} required />
                </label>
                <label>
                  Student Name:
                  <input type="text" name="studentName" defaultValue={modalData.data.studentName} required />
                </label>
                <label>
                  Father Name:
                  <input type="text" name="fatherName" defaultValue={modalData.data.fatherName} required />
                </label>
                <label>
                  Village:
                  <input type="text" name="village" defaultValue={modalData.data.village} required />
                </label>
                <label>
                  Tehsil:
                  <input type="text" name="tehsil" defaultValue={modalData.data.tehsil} required />
                </label>
                <label>
                  District:
                  <input type="text" name="district" defaultValue={modalData.data.district} required />
                </label>
                <label>
                  State:
                  <input type="text" name="state" defaultValue={modalData.data.state} required />
                </label>
                <label>
                  Contact:
                  <input type="text" name="contact" defaultValue={modalData.data.contact} required />
                </label>
                <label>
                  Gender:
                  <input type="text" name="gender" defaultValue={modalData.data.gender} required />
                </label>
                <label>
                  Transaction Details:
                  <input type="text" name="transactionDetails" defaultValue={modalData.data.transactionDetails} required />
                </label>
                <label>
                  Status:
                  <select name="status" defaultValue={modalData.data.status}>
                    <option value="PENDING">Pending</option>
                    <option value="APPROVED">Approved</option>
                  </select>
                </label>
                <button type="submit">Save Changes</button>
                <button type="button" onClick={closeModal}>
                  Cancel
                </button>
              </form>
            </div>
          </div>
        )}
      </div>
    </>
  );
  
};

export default AdmissionPage;